import React, { useEffect } from 'react';

interface PromotionalBannerProps {
  text?: string;
}

export default function PromotionalBanner({
  text = "40% OFF ALL PLANS"
}: PromotionalBannerProps) {
  useEffect(() => {
    // Inject animation styles
    const styleId = 'promotional-banner-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes promoGlowPulse {
        0%, 100% {
          box-shadow: 0 0 20px rgba(219, 39, 119, 0.3), 0 0 40px rgba(147, 51, 234, 0.2);
        }
        50% {
          box-shadow: 0 0 30px rgba(219, 39, 119, 0.4), 0 0 60px rgba(147, 51, 234, 0.3);
        }
      }
      
      @keyframes promoShineSweep {
        0% {
          transform: translateX(-100%) skewX(-15deg);
          opacity: 0;
        }
        10% {
          opacity: 0.3;
        }
        20% {
          opacity: 0.5;
        }
        30% {
          transform: translateX(200%) skewX(-15deg);
          opacity: 0;
        }
        100% {
          transform: translateX(200%) skewX(-15deg);
          opacity: 0;
        }
      }
      
      .promo-badge {
        animation: promoGlowPulse 4s ease-in-out infinite;
        max-width: calc(100% - 1rem);
        width: fit-content;
      }
      
      .promo-badge::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.2) 50%,
          transparent 100%
        );
        animation: promoShineSweep 8s ease-in-out infinite;
        pointer-events: none;
      }
      
      /* More aggressive fluid typography - scales from smaller on mobile to larger on desktop */
      .promo-emoji {
        font-size: clamp(0.625rem, 3vw + 0.25rem, 1.125rem) !important; /* 10px to 18px */
      }
      
      .promo-text {
        font-size: clamp(0.625rem, 3vw + 0.25rem, 1.125rem) !important; /* 10px to 18px */
        letter-spacing: clamp(0.01em, 0.15vw, 0.05em) !important;
      }
      
      /* More aggressive fluid padding - smaller on mobile, larger on desktop */
      .promo-badge {
        padding: clamp(0.375rem, 1.2vw + 0.2rem, 0.875rem) clamp(0.5rem, 4vw + 0.25rem, 2rem) !important;
      }
      
      /* More aggressive fluid gap - smaller on mobile */
      .promo-badge-content {
        gap: clamp(0.25rem, 1.2vw + 0.1rem, 0.625rem) !important; /* 4px to 10px */
      }
      
      /* Ensure text never overflows on any screen size */
      .promo-badge-content {
        max-width: 100%;
        overflow: hidden;
      }
      
      @media (prefers-reduced-motion: reduce) {
        .promo-badge {
          animation: none !important;
          box-shadow: 0 0 20px rgba(219, 39, 119, 0.3), 0 0 40px rgba(147, 51, 234, 0.2) !important;
        }
        
        .promo-badge::after {
          animation: none !important;
          opacity: 0 !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  return (
    <div className="w-full mb-8 px-2 sm:px-4 flex justify-center">
      <div 
        className="promo-badge relative inline-flex items-center justify-center rounded-full overflow-hidden"
        style={{
          background: 'linear-gradient(90deg, rgba(147, 51, 234, 0.6) 0%, rgba(219, 39, 119, 0.6) 100%)',
          border: '1px solid rgba(236, 72, 153, 0.5)',
        }}
      >
        <div className="promo-badge-content flex items-center justify-center whitespace-nowrap relative z-10">
          {/* Main text */}
          <span className="promo-text font-bold text-white uppercase tracking-wide px-0.5 sm:px-1">
            {text}
          </span>
        </div>
      </div>
    </div>
  );
}
